import React, { Suspense, type ReactNode } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, AuthProvider } from '@/contexts/AuthContext';
import { OrganizationProvider } from '@/contexts/OrganizationContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { DialogProvider } from '@/contexts/DialogContext';
import { MainLayout } from '@/components/layout/MainLayout';
import { LoginPage } from '@/pages/auth/LoginPage';
import { type UserRole } from '@/types/team';
import { GamutLoader } from '@/components/ui/GamutLoader';
import { EnvironmentBadge } from '@/components/ui/EnvironmentBadge';
import { ProfileLoadError } from '@/components/auth/ProfileLoadError';

const named = <T,>(key: string) =>
  (mod: Record<string, T>) => ({ default: mod[key] as T });

const GlobalDashboard = React.lazy(() => import('@/pages/dashboard/GlobalDashboard').then(named('GlobalDashboard')));
const JobsList = React.lazy(() => import('@/pages/jobs/JobsList'));
const JobDetails = React.lazy(() => import('@/pages/jobs/JobDetails').then(named('JobDetails')));
const OrgManagement = React.lazy(() => import('@/pages/org/OrgManagement').then(named('OrgManagement')));
const OperationsBoard = React.lazy(() => import('@/pages/jobs/OperationsBoard').then(named('OperationsBoard')));
const OfficeTeam = React.lazy(() => import('@/pages/team/OfficeTeam').then(named('OfficeTeam')));
const ManageDepartments = React.lazy(() => import('@/pages/org/ManageDepartments').then(named('ManageDepartments')));
const TeamManagement = React.lazy(() => import('@/pages/team/TeamManagement').then(named('TeamManagement')));
const OfficeDashboard = React.lazy(() => import('@/pages/dashboard/OfficeDashboard').then(named('OfficeDashboard')));
const DepartmentDashboard = React.lazy(() => import('@/pages/dashboard/DepartmentDashboard').then(named('DepartmentDashboard')));
const ProfilePage = React.lazy(() => import('@/pages/profile/ProfilePage').then(named('ProfilePage')));
const OnboardingWizard = React.lazy(() => import('@/pages/onboarding/OnboardingWizard').then(named('OnboardingWizard')));
const JoinPage = React.lazy(() => import('@/pages/join/JoinPage').then(named('JoinPage')));
const OfficesManagement = React.lazy(() => import('@/pages/offices/OfficesManagement').then(named('OfficesManagement')));
const DepartmentsManagement = React.lazy(() => import('@/pages/departments/DepartmentsManagement').then(named('DepartmentsManagement')));
const OperationalReports = React.lazy(() => import('@/pages/reports/OperationalReports').then(named('OperationalReports')));
const OfficeSettings = React.lazy(() => import('@/pages/offices/OfficeSettings').then(named('OfficeSettings')));
const OfficeEquipmentPage = React.lazy(() => import('@/pages/equipment/OfficeEquipmentPage').then(named('OfficeEquipmentPage')));
const MasterEquipmentCatalogPage = React.lazy(() => import('@/pages/equipment/MasterEquipmentCatalogPage').then(named('MasterEquipmentCatalogPage')));
const BillingPage = React.lazy(() => import('@/pages/billing/BillingPage').then(named('BillingPage')));
const PrivacyPolicyPage = React.lazy(() => import('@/pages/legal/PrivacyPolicyPage').then(named('PrivacyPolicyPage')));


// Redirect authenticated users away from login page
const PublicRoute: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, profile, loading, profileError, signOut } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return <GamutLoader variant="page" />;
  }

  // Failed profile read is not the same as a missing profile — do not send them
  // into onboarding.
  if (user && profileError) {
    return (
      <ProfileLoadError
        message={profileError}
        onSignOut={async () => {
          await signOut();
          navigate('/login', { replace: true });
        }}
      />
    );
  }

  // Signed in (e.g. OAuth) but no Firestore profile yet — avoid a stuck /login state
  if (user && !profile) {
    return <Navigate to="/onboarding" replace />;
  }

  if (user && profile) {
    if (!profile.onboardingCompleted) {
      return <Navigate to="/onboarding" replace />;
    }

    // WEB-75: FINANCE lands on /billing. It is org-scoped (no office or
    // department of its own) and cannot open the dashboards, so neither
    // branch below applies even when an admin gave it an office.
    if (profile.role === 'FINANCE') {
      return <Navigate to="/billing" replace />;
    }

    if (['MEMBER', 'DEPT_MANAGER', 'OFFICE_ADMIN', 'OWNER', 'ORG_ADMIN'].includes(profile.role) && profile.officeId && profile.departmentId) {
      return <Navigate to={`/office/${profile.officeId}/department/${profile.departmentId}`} replace />;
    }

    if (profile.role === 'OFFICE_ADMIN' && profile.officeId) {
      return <Navigate to={`/office/${profile.officeId}/dashboard`} replace />;
    }

    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const ProtectedRoute: React.FC<{
  children: ReactNode;
  useMainLayout?: boolean;
  requireOnboarding?: boolean;
  allowedRoles?: UserRole[];
}> = ({
  children,
  useMainLayout = true,
  requireOnboarding = true,
  allowedRoles
}) => {
    const { user, profile, loading, profileError, signOut } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    if (loading) return <GamutLoader variant="page" />;
    if (!user) return <Navigate to="/login" state={{ from: location }} replace />;

    // Failed profile read is not the same as a missing profile — do not send them
    // into onboarding.
    if (profileError) {
      return (
        <ProfileLoadError
          message={profileError}
          onSignOut={async () => {
            await signOut();
            navigate('/login', { replace: true });
          }}
        />
      );
    }

    if (!profile && location.pathname !== '/onboarding') {
      return <Navigate to="/onboarding" replace />;
    }

    if (requireOnboarding && !profile?.onboardingCompleted) {
      return <Navigate to="/onboarding" replace />;
    }

    // Redirect users hitting a restricted path to their highest allowed context.
    if (allowedRoles && profile && !allowedRoles.includes(profile.role)) {
      // 0. FINANCE → Billing (WEB-75). Its home is /billing, not `/`, which
      //    it cannot open; without this branch the fallback below would show
      //    it the Access Denied screen at the root.
      if (profile.role === 'FINANCE') {
        return <Navigate to="/billing" replace />;
      }

      // 1. Members / Dept Managers → Department Dashboard
      if (['MEMBER', 'DEPT_MANAGER'].includes(profile.role) && profile.officeId && profile.departmentId) {
        return <Navigate to={`/office/${profile.officeId}/department/${profile.departmentId}`} replace />;
      }

      // 2. Office Admins → Office Dashboard
      if (profile.role === 'OFFICE_ADMIN' && profile.officeId) {
        return <Navigate to={`/office/${profile.officeId}/dashboard`} replace />;
      }

      // 3. Fallback — prevent infinite redirect loop at root
      if (location.pathname === '/') {
        return (
          <div className="h-screen flex items-center justify-center bg-bg-primary text-text-primary flex-col gap-4">
            <h1 className="text-2xl font-bold text-red-500">Access Denied</h1>
            <p className="text-text-muted">You are logged in but do not have an assigned Office or Department.</p>
            <p className="text-sm">Please contact your administrator.</p>
            <button
              onClick={async () => {
                await signOut();
                navigate('/login', { replace: true });
              }}
              className="text-accent-electric hover:underline"
            >
              Return to Login
            </button>
          </div>
        );
      }
      return <Navigate to="/" replace />;
    }

    return useMainLayout ? <MainLayout>{children}</MainLayout> : <>{children}</>;
  };

function App() {
  return (
    <ThemeProvider>
      {/* Env indicator lives above the router so no route escapes it */}
      <EnvironmentBadge />
      <AuthProvider>
      <OrganizationProvider>
        <Router>
        <DialogProvider>
          <Suspense fallback={<GamutLoader />}>
          <Routes>
            <Route path="/login" element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            } />

            <Route path="/privacy" element={<PrivacyPolicyPage />} />

            {/* Invite Accept — fully public, handles its own auth */}
            <Route path="/join" element={<JoinPage />} />

            {/* User Onboarding */}
            <Route path="/onboarding" element={
              <ProtectedRoute useMainLayout={false} requireOnboarding={false}>
                <OnboardingWizard />
              </ProtectedRoute>
            } />

            {/* --- Global & Enterprise Context --- */}
            <Route path="/" element={<ProtectedRoute allowedRoles={['OWNER', 'ORG_ADMIN']}><GlobalDashboard /></ProtectedRoute>} />
            <Route path="/kanban" element={<ProtectedRoute allowedRoles={['OWNER', 'ORG_ADMIN', 'FINANCE']}><OperationsBoard /></ProtectedRoute>} />
            <Route path="/jobs" element={<ProtectedRoute allowedRoles={['OWNER', 'ORG_ADMIN', 'OFFICE_ADMIN', 'DEPT_MANAGER', 'MEMBER', 'FINANCE']}><JobsList /></ProtectedRoute>} />
            <Route path="/jobs/:jobId" element={<ProtectedRoute allowedRoles={['OWNER', 'ORG_ADMIN', 'OFFICE_ADMIN', 'DEPT_MANAGER', 'MEMBER', 'FINANCE']}><JobDetails /></ProtectedRoute>} />

            <Route path="/offices" element={<ProtectedRoute allowedRoles={['OWNER', 'ORG_ADMIN']}><OfficesManagement /></ProtectedRoute>} />
            <Route path="/equipment/catalog" element={<ProtectedRoute allowedRoles={['OWNER', 'ORG_ADMIN']}><MasterEquipmentCatalogPage /></ProtectedRoute>} />
            <Route path="/departments" element={<ProtectedRoute allowedRoles={['OWNER', 'ORG_ADMIN']}><DepartmentsManagement /></ProtectedRoute>} />
            <Route path="/users" element={<ProtectedRoute allowedRoles={['OWNER', 'ORG_ADMIN']}><TeamManagement /></ProtectedRoute>} />

            <Route path="/reports/ops" element={<ProtectedRoute allowedRoles={['OWNER', 'ORG_ADMIN']}><OperationalReports /></ProtectedRoute>} />
            <Route path="/settings/org" element={<ProtectedRoute allowedRoles={['OWNER', 'ORG_ADMIN']}><OrgManagement /></ProtectedRoute>} />
            <Route path="/billing" element={<ProtectedRoute allowedRoles={['OWNER', 'ORG_ADMIN', 'FINANCE']}><BillingPage /></ProtectedRoute>} />

            <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />

            {/* --- Office Context --- */}
            <Route path="/office/:officeId/dashboard" element={<ProtectedRoute allowedRoles={['OWNER', 'ORG_ADMIN', 'OFFICE_ADMIN']}><OfficeDashboard /></ProtectedRoute>} />
            <Route path="/office/:officeId/kanban" element={<ProtectedRoute allowedRoles={['OWNER', 'ORG_ADMIN', 'OFFICE_ADMIN']}><OperationsBoard /></ProtectedRoute>} />
            <Route path="/office/:officeId/ops" element={<ProtectedRoute allowedRoles={['OWNER', 'ORG_ADMIN', 'OFFICE_ADMIN']}><OperationsBoard /></ProtectedRoute>} /> {/* Legacy/Alias */}
            <Route path="/office/:officeId/jobs" element={<ProtectedRoute allowedRoles={['OWNER', 'ORG_ADMIN', 'OFFICE_ADMIN']}><JobsList /></ProtectedRoute>} />
            <Route path="/office/:officeId/jobs/:jobId" element={<ProtectedRoute allowedRoles={['OWNER', 'ORG_ADMIN', 'OFFICE_ADMIN']}><JobDetails /></ProtectedRoute>} />

            <Route path="/office/:officeId/depts" element={<ProtectedRoute allowedRoles={['OWNER', 'ORG_ADMIN', 'OFFICE_ADMIN']}><ManageDepartments /></ProtectedRoute>} />
            <Route path="/office/:officeId/departments" element={<ProtectedRoute allowedRoles={['OWNER', 'ORG_ADMIN', 'OFFICE_ADMIN']}><ManageDepartments /></ProtectedRoute>} /> {/* Alias */}
            <Route path="/office/:officeId/equipment" element={<ProtectedRoute allowedRoles={['OWNER', 'ORG_ADMIN', 'OFFICE_ADMIN']}><OfficeEquipmentPage /></ProtectedRoute>} />
            <Route path="/office/:officeId/team" element={<ProtectedRoute allowedRoles={['OWNER', 'ORG_ADMIN', 'OFFICE_ADMIN']}><OfficeTeam /></ProtectedRoute>} />

            <Route path="/office/:officeId/settings" element={<ProtectedRoute allowedRoles={['OWNER', 'ORG_ADMIN', 'OFFICE_ADMIN']}><OfficeSettings /></ProtectedRoute>} />

            <Route path="/office/:officeId/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />

            {/* --- Department Context --- */}
            <Route path="/office/:officeId/department/:departmentId" element={<ProtectedRoute allowedRoles={['OWNER', 'ORG_ADMIN', 'OFFICE_ADMIN', 'DEPT_MANAGER', 'MEMBER']}><DepartmentDashboard /></ProtectedRoute>} />
            <Route path="/office/:officeId/department/:departmentId/kanban" element={<ProtectedRoute allowedRoles={['OWNER', 'ORG_ADMIN', 'OFFICE_ADMIN', 'DEPT_MANAGER', 'MEMBER']}><OperationsBoard /></ProtectedRoute>} />
            <Route path="/office/:officeId/department/:departmentId/jobs" element={<ProtectedRoute allowedRoles={['OWNER', 'ORG_ADMIN', 'OFFICE_ADMIN', 'DEPT_MANAGER', 'MEMBER']}><JobsList /></ProtectedRoute>} />
            <Route path="/office/:officeId/department/:departmentId/jobs/:jobId" element={<ProtectedRoute allowedRoles={['OWNER', 'ORG_ADMIN', 'OFFICE_ADMIN', 'DEPT_MANAGER', 'MEMBER']}><JobDetails /></ProtectedRoute>} />
            <Route path="/office/:officeId/department/:departmentId/team" element={<ProtectedRoute allowedRoles={['OWNER', 'ORG_ADMIN', 'OFFICE_ADMIN', 'DEPT_MANAGER', 'MEMBER']}><OfficeTeam /></ProtectedRoute>} />

            <Route path="/office/:officeId/department/:departmentId/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
          </Suspense>
        </DialogProvider>
        </Router>
      </OrganizationProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
